import { useEffect, useState } from 'react';
import { AutoComplete, Avatar, Button, DatePicker, Form, Input, List, message, Select, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleFilled } from '@ant-design/icons';
import { RangePickerProps } from 'antd/lib/date-picker';
import type { Moment } from 'moment';
import moment from 'moment';
import './TaskMapForm.less';
import { getTaskTypeByUser, updateTask } from '../../../store/slice/taskSlice';
import { useAppDispatch } from '../../../store/hooks/hooks';
import User from '../../../models/User';
import Task from '../../../models/Task';
import { createInitialNames, stateTask, taskStateUsers, stateTaskUsers } from '../../../utils/functions';
import { useNavigate } from 'react-router-dom';
import TaskType from '../../../models/TaskType';
import { DispatchModel } from '../../../models/Dispatch';
import { ReactComponent as Delete } from '../../../res/svg/remove-outlined-white.svg';
import UserTask from '../../../models/UserTask';

type RangeValue = [Moment | null, Moment | null] | null;

interface TaskMapFormProps {
  task: Task;
  showTaskInformation: (show: boolean, task: Task) => void;
}

function TaskMapForm(props: TaskMapFormProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { Option } = Select;
  const { Text } = Typography;
  const { RangePicker } = DatePicker;
  const [valueAssignedUser, setValueAssignedUser] = useState<string>('');
  const [assignedUsersDataSelected, setAssignedUsersDataSelected] = useState<User[]>([]);
  const [taskTypeAll, setTaskTypeAll] = useState<TaskType[]>([])
  const [executionDateRangeValue, setExecutionDateRangeValue] = useState<RangeValue>(null);

  //Variable para saber si la tarea esta en estado pendiente o finalizado
  const isPendingOrRejectedTask = props.task.estado === stateTask.PENDIENTE || props.task.estado === stateTask.RECHAZADA;

  /*Task Type*/
  const [toggleSelectTypeTask, setToggleSelectTypeTask] = useState<Boolean>(false);
  const [taskTypeSelected, setTaskTypeSelected] = useState<TaskType>({});

  /**
   * Form actions
   */
  const getAllTaskTypes = async () => {
    const tasksTypeByUser = await dispatch(getTaskTypeByUser());
    if((tasksTypeByUser as DispatchModel).payload.status){
      const allTypeTasks:TaskType[] = (tasksTypeByUser as DispatchModel).payload.data
      setTaskTypeAll(allTypeTasks);
    } else {
      setTaskTypeAll([]);
    }
  }

  const filterAssignedUser = (taskTypeId: number) =>{
    setTaskTypeSelected(taskTypeAll.find(value => value.id === taskTypeId) as any);
  }

  const executionDatePickerOnChange: RangePickerProps['onChange'] = (
    dates,
    dateStrings,
  ) => {
    if (dates) {
      setExecutionDateRangeValue([
        moment(dateStrings[0], 'DD-MM-YYYY'),
        moment(dateStrings[1], 'DD-MM-YYYY'),
      ]);
    } else {
      setExecutionDateRangeValue(null);
    }
  };
  const cancelForm = () => {
    if(props.task?.id)
      props.showTaskInformation(true, props.task)
    else
      navigate('/tareas');
  };
  const saveUserTask = () => {
    form
      .validateFields()
      .then((values) => {
        let task: Task = {};
        if (values) {
          if(isPendingOrRejectedTask){
            task = {
              id: props.task.id,
              tareaUsuarioId: assignedUsersDataSelected.map(({ id }) => id!)
            } 
          } else {
            task = {
              id: props.task.id,
              titulo: values.titulo ? values.titulo : null,
              descripcion: values.descripcion ? values.descripcion : null,
              observaciones: values.observaciones ? values.observaciones : null, 
              fechaDesde: executionDateRangeValue?.[0] ? moment(executionDateRangeValue?.[0], 'YYYY-MM-DD').format() : null, 
              fechaHasta: executionDateRangeValue?.[1] ? moment(executionDateRangeValue?.[1], 'YYYY-MM-DD').format() : null,
              tipotareaId: values.tipotareaId,
              tareaUsuarioId: assignedUsersDataSelected.map(({ id }) => id!)
            }
          }
          dispatch(updateTask(task)).then((value: any) => {
            if(value.payload.status){
              props.showTaskInformation(true, value.payload.data);
              message.success('Se modifico la tarea correctamente');
            }
            else {
              message.error(value.payload.data?.message);
            }
          });
        }
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  /**
  *Funcion que permite selecionar usuarios y asignarlos a la tarea 
  */
  const onSelectAssignedUser = (email: string) =>{

    /*Verifica si el usuario seleccionado se encuentra en la lista a partir del email*/
    const verifyUsersSelected = assignedUsersDataSelected.findIndex(
      (user: User) => user.email === email,
    );

    /**
     * si no se encuentra el usuario se agrega al arreglo de usuarios seleccionados
     */
    if (verifyUsersSelected === -1) {
      let matchUser = taskTypeSelected.usuariosByTipoTarea?.find(user => user.email === email);
      //se puede borrar de la lista
      matchUser = {...matchUser, isRemovable: true};
      if(matchUser?.email){
        setAssignedUsersDataSelected([
          ...assignedUsersDataSelected,
          matchUser,
        ]);
        }
    }
  }

  const searchUsers = (input: string) =>{
    setValueAssignedUser(input);
  }

  const removeUserAssigned = (user: User) => {
    const verifyAssignedUsers = assignedUsersDataSelected;
    let key = verifyAssignedUsers.findIndex((x: any) => x.id === user.id);
    if (key !== -1) {
      verifyAssignedUsers.splice(key, 1);
      setAssignedUsersDataSelected([...verifyAssignedUsers]);
    }
  };

  const handleChange = (value: number, option: any) => {
    form.setFieldsValue({
      tipotareaId: value,
    })
    setToggleSelectTypeTask(!toggleSelectTypeTask);
    if(assignedUsersDataSelected.length > 0){
      message.warning(
        `Las personas asignadas han sido 
        removidas porque cambiaste el tipo de tarea. 
        Por favor asígnalas de nuevo.`);
    }
    setAssignedUsersDataSelected([]);
    
  };

  const isRemovable = (task: Task, user: UserTask) =>{
    let isRemovable = true;
    if(isPendingOrRejectedTask || 
      (task.estado === stateTask.ASIGNADA && user.estado === stateTaskUsers.RECHAZADA)){
      isRemovable = false;
    }
    return isRemovable
  }

  useEffect(() => {
    /**Verifica si tiene una tarea creada para cambiar
     * a modo editar
     */
    if (Object.keys(props.task).length !== 0) {
      let dataEdit: Task = props.task;
      //Se incluye el estado que viene el usuario con la tarea y un nuevo atributo isRemovable
      let usersAssigned = dataEdit.tareaUsuario?.map((userTask)=>({
        ...userTask.usuario, estado: userTask.estado, isRemovable: isRemovable(dataEdit, userTask)
      })) as User[] || [];

      setAssignedUsersDataSelected(usersAssigned);

      if(dataEdit?.fechaDesde){

        const fechaDesde:Date = new Date(dataEdit.fechaDesde + 'T00:00:00');
        const fechaHasta:Date = new Date(dataEdit.fechaHasta + 'T00:00:00');
        
        setExecutionDateRangeValue([
          moment(fechaDesde, 'DD/MM/YYYY'),
          moment(fechaHasta, 'DD/MM/YYYY'),
        ]);
      }

      form.setFieldsValue({
        titulo: dataEdit.titulo,
        tipotareaId: dataEdit.tipotareaId,
        descripcion: dataEdit.descripcion,
        observaciones: dataEdit.observaciones,
        tareaUsuarioId: dataEdit.tareaUsuario,
      });
    }

    const taskTypeId = form.getFieldValue('tipotareaId');
    getAllTaskTypes().then(()=>{
      filterAssignedUser(taskTypeId);
    });
  }, [form, props.task, ]);

  useEffect(() => {
    const currentTypeTaskId = form.getFieldValue('tipotareaId');
    filterAssignedUser(currentTypeTaskId);
  }, [toggleSelectTypeTask, taskTypeAll]);

  return (
    <div className='tp-task-map__form__content'>
      <div className='tp-task-map__form__inner_content'>
        <Form layout={'vertical'} form={form} name='addTaskForm'>
          <Form.Item
            name={'titulo'}
            required={true}
            label='Nombre de la Tarea:'
            rules={[
              {
                required: true,
                message: 'Por favor ingrese el nombre de la tarea.',
              },
            ]}
          >
            <Input
              required
              disabled={isPendingOrRejectedTask}
              placeholder={'Ingresa un nombre'}
              value={''}
              className='tp-task-map__form__content_input'
              size='large'
            />
          </Form.Item>
          <Form.Item
            required={true}
            name={'tipotareaId'}
            label='Tipo de Tarea:'
            rules={[
              {
                required: true,
                message: 'Por favor seleccione el tipos de tarea.',
              },
            ]}
          >
            <Select
              disabled={isPendingOrRejectedTask}
              placeholder="Selecciona una tarea para continuar"
              optionFilterProp="children"
              onChange={handleChange}
            > 
              {taskTypeAll.map(
                (item) =>
                  item && (
                    <Option value={item.id} key={item.id}>
                      {item.tipotarea}
                    </Option>
                  )
              )}
            </Select>
          </Form.Item>
          <Form.Item name={'descripcion'} label='Descripción:'>
            <Input.TextArea
              required
              disabled={isPendingOrRejectedTask}
              placeholder={'Ingresa la descripción de la tarea'}
              value={''}
              className='tp-task-map__form__content_inputarea textarea-large'
            />
          </Form.Item>
          <Form.Item name={'observaciones'} label='Observaciones:'>
            <Input.TextArea
              required
              disabled={isPendingOrRejectedTask}
              placeholder={'Ingresa la observación de la tarea'}
              value={''}
              rows={7}
              className='tp-task-map__form__content_inputarea'
            />
          </Form.Item>
          <div className='tp-task-map__filter__dates__eject'>
            <Text className='label'>Realización:</Text>
            <div className='container'>
              <div className='date-picker__container'>
                <RangePicker
                  disabled={isPendingOrRejectedTask}
                  size='large'
                  format={'DD/MM/YYYY'}
                  className='range__date-picker'
                  placeholder={["Fecha inicial","Fecha limite"]}
                  onChange={executionDatePickerOnChange}
                  value={
                    executionDateRangeValue !== null
                      ? executionDateRangeValue
                      : null
                  }
                />
              </div>
            </div>
          </div>
          <Text className={'text-assigned'}>
              Asignado(s):
          </Text>
          <div className={'container-info-task'}>
            <div className={'icon'}>
              <InfoCircleFilled style={{color: '#0078A0'}}/>
            </div>
            <Text>
              Ten en cuenta que cuando al menos una persona asignada acepte la tarea, 
              ya no podrás editarla.
            </Text>
          </div>
          <Form.Item>
            <div id={'assignedUserInputContainer'}>
            <AutoComplete
              allowClear={true}
              placeholder='Buscar para asignar'
              size='large'
              onSearch={searchUsers}
              onSelect={onSelectAssignedUser}
              value={valueAssignedUser}
              //Allow put the popup relative to the parent
              getPopupContainer={() => document.getElementById("assignedUserInputContainer") as any}
              filterOption={(inputValue, option) =>
                option!.title
                  .toLowerCase()
                  .indexOf(inputValue.toLowerCase()) !== -1
              }
              dropdownAlign={{
                points: ["tl", "bl"], // align dropdown bottom-left to top-left of input element
                offset: [0, 0], // align offset in point 0
                overflow: {
                  adjustX: 0,
                  adjustY: 0 // do not auto flip in y-axis
                }
              }}
            >
              {/**De acuerdo al endpoint, recibe un email como valor unico del usuario */}
              {taskTypeSelected?.usuariosByTipoTarea && taskTypeSelected?.usuariosByTipoTarea?.map((user: User) => (
                <Option key={user.id} value={user.email} title={`${user.nombre} ${user.apellido}`}>
                  <div className='tp-task-map__assigned__user'>
                    <div className='tp-task-map__assigned__user_information'>
                      <Avatar style={{ backgroundColor: '#BFBFBF' }}>
                        {createInitialNames(user.nombre, user.apellido)}
                      </Avatar>
                      <Text className='email'>{user.nombre} {user.apellido}</Text>
                    </div>
                    <Tag className='add'>Agregar</Tag>
                  </div>
                </Option>
              ))}
            </AutoComplete>
              <List
                id={'list-users'}
                dataSource={assignedUsersDataSelected}
                renderItem={(user: User) => (
                    <List.Item key={user.id} className='tp-task-map__assigned__user__list'>
                      <div className='tp-task-map__assigned__user'>
                        <div className='tp-task-map__assigned__user_information'>
                          <Avatar style={{ backgroundColor: '#BFBFBF' }}>
                            {createInitialNames(user.nombre, user.apellido)}
                          </Avatar>
                          <Text className='email' title={`${user.nombre} ${user.apellido}`}>
                            {user.nombre} {user.apellido}
                          </Text>
                        </div>
                        {
                          user.isRemovable ?
                          <Tag
                          className={'delete-user'}
                          icon={<Delete/>}
                          onClick={() => removeUserAssigned(user)} /> 
                          :
                          <Tag className={taskStateUsers(user?.estado as number).className}>
                          {taskStateUsers(user?.estado as number).title}
                          </Tag> 
                        }
                      </div>
                  </List.Item>
                )}
              />
            </div>  
          </Form.Item>
        </Form>
      </div>
      <div className='tp-task-map__form__content_actions'>
        <Button
          shape='round'
          icon={<CloseOutlined />}
          onClick={() => {
            cancelForm();
          }}
        >
          Cancelar
        </Button>
        <Button
          type='primary'
          shape='round'
          icon={<CheckOutlined />}
          onClick={() => {
            saveUserTask();
          }}
        >
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

export default TaskMapForm;
